import React, { useState, useEffect } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input, Button,
    Row, Col, Card, CardBody, Badge,
} from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
    getEvaluations          as onGetEvaluations,
    getStudyLevels          as onGetStudyLevels,
    addNewExamRoomAssignment as onAddNewAssignment,
    resetExamRoomAssignmentLastActionSuccess as OnResetExamRoomAssignmentLastActionSuccess,
} from "store/actions";

import { selectCustomStyles } from "../../../common/selectCustomStyles";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const AssignmentModal = ({ isOpen, toggle, examRoom }) => {
    const dispatch = useDispatch();

    const [date,       setDate]       = useState("");
    const [startTime,  setStartTime]  = useState("");
    const [endTime,    setEndTime]    = useState("");
    const [evaluation, setEvaluation] = useState(null);
    const [configs,    setConfigs]    = useState([]); // config par classe

    

    const Properties = createSelector(
        (state) => ({
            evaluations: state.evaluationReducer.evaluations || [],
            studyLevels: state.studyLevelReducer.studyLevels || [],
        }),
        (s) => s
    );

    const { evaluations, studyLevels } = useSelector(Properties);

    useEffect(() => {
        if (isOpen) {
            dispatch(onGetEvaluations());
            dispatch(onGetStudyLevels());
        }
    }, [isOpen, dispatch]);


    const { success, error } = useSelector((state) => state.examRoomAssignmentReducer);

 
    // Ferme le modal en cas de success
    useEffect(() => {
        // Si le dispatch dans onSubmit a réussi (success passe à true)
        if (success && isOpen) {
            toggle(); // On ferme le modal
            dispatch(OnResetExamRoomAssignmentLastActionSuccess()); // On remet success à false immédiatement
        }
    }, [success, isOpen, toggle, dispatch]);



    // Calculer total places configurées
    const totalConfigured = configs.reduce(
        (sum, c) => sum + (parseInt(c.max_students) || 0), 0
    );
    const capacityOk = examRoom
        ? totalConfigured <= examRoom.capacity
        : true;

    const handleAddConfig = () => {
        setConfigs([...configs, {
            study_level_id: null,
            max_students:   "",
            alphabet_start: "A",
            alphabet_end:   "Z",
        }]);
    };

    const handleRemoveConfig = (index) => {
        setConfigs(configs.filter((_, i) => i !== index));
    };

    const handleConfigChange = (index, field, value) => {
        const updated = [...configs];
        updated[index][field] = value;
        setConfigs(updated);
    };

    const handleSubmit = () => {
        if (!evaluation || !date || !startTime || !endTime || configs.length === 0) return;

        dispatch(onAddNewAssignment({
            exam_room_id:  examRoom.id,
            evaluation_id: evaluation.value,
            date,
            start_time:    startTime,
            end_time:      endTime,
            class_configs: configs.map((c) => ({
                study_level_id: c.study_level_id?.value,
                max_students:   parseInt(c.max_students),
                alphabet_start: c.alphabet_start,
                alphabet_end:   c.alphabet_end,
            })),
        }));
        //toggle();
    };

    const evaluationOptions = evaluations.map((e) => ({
        value: e.id,
        label: e.title,
    }));

    const studyLevelOptions = studyLevels.map((l) => ({
        value: l.id,
        label: l.name,
    }));

    const alphabetOptions = ALPHABET.map((l) => ({
        value: l,
        label: l,
    }));

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
            <ModalHeader toggle={toggle}>
                Programmer la salle —{" "}
                <span className="text-primary">{examRoom?.name}</span>
                <Badge color="info" className="ms-2">
                    {examRoom?.capacity} places
                </Badge>
            </ModalHeader>

            <ModalBody>
                <Form>
                    {/* Évaluation */}
                    <FormGroup>
                        <Label>Évaluation *</Label>
                        <Select
                            options={evaluationOptions}
                            value={evaluation}
                            onChange={setEvaluation}
                            placeholder="Sélectionner une évaluation"
                            isClearable
                            styles={selectCustomStyles}
                        />
                    </FormGroup>

                    {/* Date + Horaires */}
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Date *</Label>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Heure début *</Label>
                                <Input
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Heure fin *</Label>
                                <Input
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Configuration des classes */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Label className="mb-0 fw-bold">
                            Configuration des classes
                        </Label>
                        <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={handleAddConfig}
                        >
                            <i className="bx bx-plus me-1"></i>
                            Ajouter une classe
                        </Button>
                    </div>

                    {/* Indicateur capacité */}
                    {configs.length > 0 && (
                        <div className={`alert ${capacityOk ? "alert-info" : "alert-danger"} py-2 mb-3`}>
                            Places configurées : <strong>{totalConfigured}</strong> / {examRoom?.capacity}
                            {!capacityOk && " ⚠️ Dépasse la capacité de la salle !"}
                        </div>
                    )}

                    {configs.map((config, index) => (
                        <Card key={index} className="mb-2 border">
                            <CardBody className="py-2">
                                <Row className="align-items-center">

                                    {/* Classe */}
                                    <Col md={4}>
                                        <Label className="small text-muted">Classe *</Label>
                                        <Select
                                            options={studyLevelOptions}
                                            value={config.study_level_id}
                                            onChange={(v) => handleConfigChange(index, "study_level_id", v)}
                                            placeholder="Sélectionner..."
                                            isClearable
                                        />
                                    </Col>

                                    {/* Max étudiants */}
                                    <Col md={2}>
                                        <Label className="small text-muted">Max étudiants *</Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            value={config.max_students}
                                            onChange={(e) => handleConfigChange(index, "max_students", e.target.value)}
                                            placeholder="Ex: 30"
                                        />
                                    </Col>

                                    {/* Alphabet début */}
                                    <Col md={2}>
                                        <Label className="small text-muted">De (A-Z) *</Label>
                                        <Select
                                            options={alphabetOptions}
                                            value={{ value: config.alphabet_start, label: config.alphabet_start }}
                                            onChange={(v) => handleConfigChange(index, "alphabet_start", v.value)}
                                        />
                                    </Col>

                                    {/* Alphabet fin */}
                                    <Col md={2}>
                                        <Label className="small text-muted">À (A-Z) *</Label>
                                        <Select
                                            options={alphabetOptions}
                                            value={{ value: config.alphabet_end, label: config.alphabet_end }}
                                            onChange={(v) => handleConfigChange(index, "alphabet_end", v.value)}
                                        />
                                    </Col>

                                    {/* Supprimer */}
                                    <Col md={1} className="text-end">
                                        <Button
                                            color="danger"
                                            size="sm"
                                            outline
                                            onClick={() => handleRemoveConfig(index)}
                                            className="mt-3"
                                        >
                                            <i className="bx bx-trash"></i>
                                        </Button>
                                    </Col>

                                </Row>
                            </CardBody>
                        </Card>
                    ))}

                    {configs.length === 0 && (
                        <div className="text-center text-muted py-3 border rounded">
                            Cliquez sur "Ajouter une classe" pour configurer les classes de cette salle
                        </div>
                    )}
                </Form>
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" outline onClick={toggle}>
                    Annuler
                </Button>
                <Button
                    color="success"
                    onClick={handleSubmit}
                    disabled={
                        !evaluation      ||
                        !date            ||
                        !startTime       ||
                        !endTime         ||
                        configs.length === 0 ||
                        !capacityOk
                    }
                >
                    Enregistrer la programmation
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AssignmentModal;